import React, { useState, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { 
  FaExclamationTriangle, 
  FaCheck, 
  FaSpinner, 
  FaUpload, 
  FaTrash, 
  FaImage, 
  FaInfoCircle,
  FaTelegram
} from 'react-icons/fa';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { ticketService } from '../services/api';

const PageHeader = styled.div`
  margin-bottom: 2.5rem;
`;

const Title = styled.h1`
  font-size: 2.2rem;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.75rem;
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    width: 60px;
    height: 4px;
    background: ${props => props.theme.colors.gradient1};
    bottom: -10px;
    left: 0;
    border-radius: 2px;
  }
`;

const Subtitle = styled.p`
  color: ${props => props.theme.colors.textMuted};
  margin-bottom: 2rem;
  line-height: 1.6;
  max-width: 700px;
  font-size: 1.1rem;
`;

const StyledForm = styled.form`
  display: grid;
  gap: 2rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Label = styled.label`
  font-weight: 600;
  font-size: 1.05rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  svg {
    color: ${props => props.theme.colors.primary};
  }
`;

const inputFocus = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const baseInputStyles = css`
  padding: 1rem 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  font-size: 1rem;
  background-color: rgba(26, 26, 26, 0.5);
  color: ${props => props.theme.colors.text};
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: transparent;
    box-shadow: 0 0 0 2px rgba(108, 92, 231, 0.5);
    background-color: rgba(30, 30, 36, 0.8);
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      border-radius: 10px;
      padding: 2px;
      background: ${props => props.theme.colors.gradient1};
      -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      animation: ${inputFocus} 3s ease infinite;
    }
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const Input = styled.input`
  ${baseInputStyles}
  height: 3.5rem;
`;

const Textarea = styled.textarea`
  ${baseInputStyles}
  min-height: 150px;
  resize: vertical;
  line-height: 1.6;
`;

const Hint = styled.p`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.textMuted};
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  svg {
    color: ${props => props.theme.colors.primary};
    font-size: 0.9rem;
  }
`;

const SuccessMessage = styled(Card)`
  background: linear-gradient(145deg, rgba(37, 176, 129, 0.1), rgba(37, 176, 129, 0.05));
  backdrop-filter: blur(10px);
  border: 1px solid rgba(37, 176, 129, 0.2);
  padding: 1.75rem;
  display: flex;
  align-items: center;
  gap: 1.25rem;
  margin-bottom: 2.5rem;
  border-radius: 12px;
  
  svg {
    color: ${props => props.theme.colors.success};
    font-size: 2rem;
    flex-shrink: 0;
  }
`;

const ErrorMessage = styled(Card)`
  background: linear-gradient(145deg, rgba(231, 76, 60, 0.1), rgba(231, 76, 60, 0.05));
  backdrop-filter: blur(10px);
  border: 1px solid rgba(231, 76, 60, 0.2);
  padding: 1.75rem;
  display: flex;
  align-items: center;
  gap: 1.25rem;
  margin-bottom: 2.5rem;
  border-radius: 12px;
  
  svg {
    color: ${props => props.theme.colors.error};
    font-size: 2rem;
    flex-shrink: 0;
  }
`;

const RequiredIndicator = styled.span`
  color: ${props => props.theme.colors.error};
  margin-left: 0.25rem;
`;

const ButtonWithSpinner = styled(Button)`
  position: relative;
  padding: 1rem 2rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  
  & > svg {
    margin-right: 0.75rem;
  }
  
  & > .spinner {
    animation: spin 1s linear infinite;
    
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  }
`;

// Nouvelle composante pour le bouton d'upload
const UploadButtonContainer = styled.div`
  margin-bottom: 1.5rem;
`;

const UploadButton = styled.label`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 1rem 1.5rem;
  background: linear-gradient(145deg, rgba(108, 92, 231, 0.1), rgba(108, 92, 231, 0.2));
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid rgba(108, 92, 231, 0.3);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
    background: linear-gradient(145deg, rgba(108, 92, 231, 0.2), rgba(108, 92, 231, 0.3));
  }
  
  svg {
    margin-right: 0.75rem;
    font-size: 1.2rem;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const ImagePreviewsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 1.25rem;
`;

const ImagePreview = styled.div`
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.03);
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const DeleteButton = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: all 0.3s ease;
  
  ${ImagePreview}:hover & {
    opacity: 1;
  }
  
  &:hover {
    background-color: ${props => props.theme.colors.error};
    transform: rotate(90deg);
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    color: white;
    font-size: 1.5rem;
    animation: spin 1s linear infinite;
    
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  }
`;

const UploadProgress = styled.div`
  height: 6px;
  background-color: rgba(108, 92, 231, 0.2);
  border-radius: 3px;
  margin-top: 1rem;
  overflow: hidden;
  
  div {
    height: 100%;
    background: ${props => props.theme.colors.gradient1};
    width: ${props => props.progress}%;
    transition: width 0.3s ease;
  }
`;

// Modal d'information
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
`;

const ModalContent = styled.div`
  background: linear-gradient(145deg, rgba(30, 30, 36, 0.95), rgba(20, 20, 26, 0.95));
  border-radius: 16px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  position: relative;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transform: ${props => props.isOpen ? 'scale(1)' : 'scale(0.9)'};
  transition: all 0.3s ease;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  
  svg {
    color: ${props => props.theme.colors.primary};
    font-size: 1.8rem;
    margin-right: 1rem;
  }
`;

const ModalTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
`;

const ModalBody = styled.div`
  color: ${props => props.theme.colors.textMuted};
  line-height: 1.7;
  font-size: 1rem;
  margin-bottom: 1.5rem;
  
  a {
    color: ${props => props.theme.colors.primary};
    text-decoration: none;
    font-weight: 600;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const InfoIcon = styled(FaInfoCircle)`
  color: ${props => props.theme.colors.primary};
  cursor: pointer;
  margin-left: 0.5rem;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.2);
  }
`;

const CloseButton = styled(Button)`
  padding: 0.75rem 1.5rem;
`;

// Ajout d'une animation de rotation pour les icônes spinner
const spinAnimation = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

// Style pour les icônes de chargement
const LoadingIcon = styled.span`
  .spinner {
    animation: ${spinAnimation} 1s linear infinite;
    display: inline-block;
  }
`;

const ReportForm = () => {
  const initialFormState = {
    reporterTelegram: '',
    scammerTelegram: '',
    description: '',
    evidence: '',
  };

  const [formData, setFormData] = useState(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  // État pour les images
  const [selectedImages, setSelectedImages] = useState([]);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // État pour la modal d'information
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };
  
  // Gestion de la sélection des images
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    
    // Vérifier le nombre maximum d'images (10)
    if (selectedImages.length + files.length > 10) {
      setError("Vous ne pouvez pas télécharger plus de 10 images. Pour plus d'images, contactez un admin.");
      return;
    }
    
    // Vérifier la taille des fichiers (max 20MB par fichier)
    const MAX_SIZE = 20 * 1024 * 1024; // 20MB
    const oversizedFiles = files.filter(file => file.size > MAX_SIZE);
    if (oversizedFiles.length > 0) {
      setError(`Les fichiers suivants sont trop volumineux: ${oversizedFiles.map(f => f.name).join(', ')}. La taille maximale est de 20MB.`);
      return;
    }
    
    // Créer des objets URL pour la prévisualisation
    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    
    setSelectedImages(prev => [...prev, ...newImages]);
    setError(null);
    
    // Réinitialiser l'input pour permettre la sélection du même fichier
    e.target.value = null;
  };
  
  // Supprimer une image
  const handleRemoveImage = (index) => {
    setSelectedImages(prev => {
      const newArray = [...prev];
      // Libérer l'URL de prévisualisation
      URL.revokeObjectURL(newArray[index].preview);
      newArray.splice(index, 1);
      return newArray;
    });
  };
  
  // Télécharger les images sélectionnées sur le serveur
  const uploadImages = async () => {
    if (selectedImages.length === 0) return [];
    
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      selectedImages.forEach(img => {
        formData.append('evidence', img.file);
      });
      
      const response = await ticketService.uploadImages(formData);
      setUploadProgress(100);
      
      // Libérer les URLs de prévisualisation
      selectedImages.forEach(img => URL.revokeObjectURL(img.preview));
      
      // Assurez-vous que les fichiers sont bien stockés au format approprié
      console.log('Files received from server:', response.data.files);
      return response.data.files || [];
    } catch (err) {
      console.error('Error uploading images:', err);
      setError("Erreur lors du téléchargement des images. Veuillez réessayer.");
      return [];
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      // D'abord, uploader les images
      const uploadedImageFiles = await uploadImages();
      
      // Adapter les noms de champs aux attentes du backend
      const reportData = {
        telegramUsername: formData.reporterTelegram,
        scammerTelegramUsername: formData.scammerTelegram,
        reason: formData.description,
        // Ne pas inclure les noms originaux pour éviter la confusion
        // et les erreurs potentielles
        evidenceImageIds: uploadedImageFiles
      };
      
      console.log('Sending report data to API:', JSON.stringify(reportData));
      
      await ticketService.create(reportData);
      setSubmitSuccess(true);
      setFormData(initialFormState);
      setSelectedImages([]);
      window.scrollTo(0, 0);
    } catch (err) {
      console.error('Error submitting report:', err);
      setError(err.response?.data?.message || 'Échec de l\'envoi du signalement. Veuillez réessayer.');
      window.scrollTo(0, 0);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Ouvrir/fermer la modal d'information
  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  return (
    <>
      <PageHeader>
        <Title>Signaler un Scammer</Title>
        <Subtitle>
          Aidez notre communauté en signalant les scammers. Votre signalement sera examiné par nos admins.
        </Subtitle>
      </PageHeader>
      
      {submitSuccess && (
        <SuccessMessage>
          <FaCheck />
          <div>
            <strong>Votre signalement a été soumis avec succès !</strong>
            <p>Nos admins examineront votre signalement dès que possible.</p>
          </div>
        </SuccessMessage>
      )}
      
      {error && (
        <ErrorMessage>
          <FaExclamationTriangle />
          <div>
            <strong>Erreur lors de l'envoi de votre signalement :</strong>
            <p>{error}</p>
          </div>
        </ErrorMessage>
      )}
      
      <Card>
        <StyledForm onSubmit={handleSubmit}>          
          <FormGroup>
            <Label>
              <FaTelegram /> Votre Username Telegram<RequiredIndicator>*</RequiredIndicator>
            </Label>
            <Input
              type="text"
              name="reporterTelegram"
              value={formData.reporterTelegram}
              onChange={handleChange}
              required
              placeholder="Entrez votre username Telegram (sans @)"
            />
            <Hint>
              <FaInfoCircle /> Votre username nous permet de vous contacter si nécessaire pour vérification.
            </Hint>
          </FormGroup>
          
          <FormGroup>
            <Label>
              <FaTelegram /> Username Telegram du Scammer<RequiredIndicator>*</RequiredIndicator>
            </Label>
            <Input
              type="text"
              name="scammerTelegram"
              value={formData.scammerTelegram}
              onChange={handleChange}
              required
              placeholder="Entrez l'username Telegram du scammer (sans @)"
            />
          </FormGroup>
          
          <FormGroup>
            <Label>
              <FaExclamationTriangle /> Description du Scam<RequiredIndicator>*</RequiredIndicator>
            </Label>
            <Textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              placeholder="Fournissez une description détaillée du scam. Incluez ce qui s'est passé, quand cela s'est produit, et comment le scam a été réalisé."
            />
          </FormGroup>
          
          <FormGroup>
            <Label>
              Preuves Textuelles (Optionnel)
            </Label>
            <Textarea
              name="evidence"
              value={formData.evidence}
              onChange={handleChange}
              placeholder="Fournissez toute preuve textuelle comme des détails de conversation ou des informations sur la transaction."
            />
          </FormGroup>
          
          {/* Section upload d'images */}
          <FormGroup>
            <Label>
              Preuves Visuelles (Optionnel)
              <InfoIcon onClick={toggleModal} />
            </Label>
            <HiddenInput 
              type="file" 
              id="image-upload" 
              accept="image/jpeg,image/png,image/gif,image/webp" 
              multiple 
              onChange={handleImageSelect}
            />
            <UploadButtonContainer>
              <UploadButton htmlFor="image-upload">
                <FaUpload /> Télécharger des images (max 10)
              </UploadButton>
            </UploadButtonContainer>
            <Hint>
              <FaInfoCircle /> Formats acceptés: JPG, PNG, GIF, WEBP. Taille maximale: 20MB par image.
            </Hint>
            
            {selectedImages.length > 0 && (
              <>
                {isUploading && (
                  <UploadProgress progress={uploadProgress}>
                    <div />
                  </UploadProgress>
                )}
                
                <ImagePreviewsContainer>
                  {selectedImages.map((img, index) => (
                    <ImagePreview key={index}>
                      <img src={img.preview} alt={`Preview ${index}`} />
                      <DeleteButton 
                        type="button" 
                        onClick={() => handleRemoveImage(index)}
                        disabled={isUploading}
                      >
                        <FaTrash size={12} />
                      </DeleteButton>
                      {isUploading && (
                        <LoadingOverlay>
                          <FaSpinner />
                        </LoadingOverlay>
                      )}
                    </ImagePreview>
                  ))}
                </ImagePreviewsContainer>
              </>
            )}
          </FormGroup>
          
          <ButtonWithSpinner type="submit" disabled={isSubmitting || isUploading}>
            {isSubmitting ? (
              <>
                <LoadingIcon>
                  <FaSpinner className="spinner" />
                </LoadingIcon>
                Envoi en cours...
              </>
            ) : (
              <>
                <FaExclamationTriangle />
                Soumettre le Signalement
              </>
            )}
          </ButtonWithSpinner>
        </StyledForm>
      </Card>
      
      {/* Modal d'information */}
      <ModalOverlay isOpen={isModalOpen} onClick={toggleModal}>
        <ModalContent isOpen={isModalOpen} onClick={e => e.stopPropagation()}>
          <ModalHeader>
            <FaInfoCircle />
            <ModalTitle>Limite d'images</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <p>Pour des raisons de performance et d'efficacité, vous ne pouvez télécharger que 10 images maximum par signalement.</p>
            <p>Si vous avez plus de preuves visuelles, veuillez contacter directement un administrateur sur notre canal Telegram <a href="https://t.me/holyhubs" target="_blank" rel="noopener noreferrer">HolyHubs</a> en mentionnant votre signalement.</p>
            <p>Nos administrateurs vous aideront à traiter les preuves supplémentaires.</p>
          </ModalBody>
          <ModalFooter>
            <CloseButton onClick={toggleModal} variant="primary">Compris</CloseButton>
          </ModalFooter>
        </ModalContent>
      </ModalOverlay>
    </>
  );
};

export default ReportForm; 